import { PlayCircleIcon } from '@langchain/untitled-ui-icons';
import Alert from '@mui/joy/Alert';
import LinearProgress from '@mui/joy/LinearProgress';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { z } from 'zod';

import Breadcrumbs from '@/components/Breadcrumbs.tsx';
import { CopyMultiButton } from '@/components/CopyButton/CopyMultiButton.tsx';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert.tsx';
import { HostProjectSettingsModalWithButton } from '@/components/HostProjectCrudPane/HostProjectSettingsModalWithButton.tsx';
import { getRevisionSimpleStatus } from '@/components/HostRevisionStatus/HostRevisionStatus.tsx';
import { HostRevisionsTable } from '@/components/HostRevisionsTable/HostRevisionsTable.tsx';
import { LatestRevisionWarningButton } from '@/components/HostRevisionsTable/LatestRevisionWarningButton.tsx';
import { UncontrolledRunsTable } from '@/components/RunsTable/UncontrolledRunsTable.tsx';
import { TabGroup } from '@/components/Tabs/Tabs.tsx';
import { TextOverflowTooltip } from '@/components/TextOverflowTooltip.tsx';
import {
  Button,
  IconButton,
  TabLabel,
  TabList,
  TabPanel,
  TabPanels,
} from '@/design-system/index.ts';
import { useDataGridState } from '@/hooks/useDataGridState.tsx';
import { usePermissions } from '@/hooks/usePermissions.tsx';
import { useStateFromSearchParams } from '@/hooks/useStateFromSearchParams.tsx';
import {
  useHostProject,
  useHostRevisions,
  useOrganizationId,
  usePrebuiltDashboardForSession,
} from '@/hooks/useSwr.tsx';
import ArrowRightIcon from '@/icons/ArrowRightIcon.svg?react';
import ProjectIcon from '@/icons/ProjectIcon';
import RocketShipIcon from '@/icons/RocketShip.svg?react';
import { HostProjectSchema, HostRevisionSchema } from '@/types/schema.ts';
import {
  appDashboardsIndexPath,
  appGraphIndexPath,
  appOrganizationPath,
  appProjectsPath,
  appSessionPath,
} from '@/utils/constants.tsx';
import { useLocalStorageState } from '@/utils/use-local-storage-state.tsx';
import { useResizeObserver } from '@/utils/use-resize-observer.tsx';

import { PageTitle } from '../../components/PageTitle.tsx';
import { RevisionCrudModalWithButton } from '../../components/RevisionsCrudModal/RevisionCrudModalWithButton.tsx';
import { LangGraphContext } from '../Graph/src/api/index.ts';
import { STUDIO_HEADERS_LOCAL_STORAGE_KEY } from '../Graph/src/constants.ts';
import { useCreateStudioClient } from '../Graph/src/hooks/useCreateStudioClient.ts';
import { useDeploymentFormType } from '../HostProjects/hooks/useDeploymentFormType.ts';
import { Chart } from '../SingleDashboard/CustomChart.tsx';
import { useChartTimeFilter } from '../SingleDashboard/hooks/useChartTimeFilter.tsx';
import { useSeriesColorsMap } from '../SingleDashboard/utils/getSeriesColor.ts';
import { AgentsPanel } from './components/AgentsPanel/index.tsx';
import { CronsPanel } from './components/CronsPanel/index.tsx';
import { MigrationBanner } from './components/MigrationBanner.tsx';
import { MonitoringPanel } from './components/MonitoringPanel/index.tsx';
import { ProjectStats } from './components/ProjectStats.tsx';
import { ThreadsPanel } from './components/ThreadsPanel/index.tsx';
import { OLD_CLUSTERS } from './constants.tsx';

dayjs.extend(utc);

function NoRevisions() {
  return (
    <div>
      <Alert variant="outlined" color="danger">
        There are no revisions.
      </Alert>
    </div>
  );
}

const HostProjectRunsTable = (props: { hostProject: HostProjectSchema }) => {
  const runsFilter = useMemo(
    () => ({
      session: [props.hostProject.tracer_session_id],
      start_time: dayjs.utc().subtract(7, 'days').toISOString(),
      is_root: true,
    }),
    [props.hostProject.tracer_session_id]
  );
  if (!props.hostProject.tracer_session_id) return null;
  return (
    <UncontrolledRunsTable
      tableDisplay="list"
      runsFilter={runsFilter}
      withToolbar={false}
      withPagination={false}
      runTableSessionId={props.hostProject.tracer_session_id}
    />
  );
};

function HostProjectRevisionsCard(props: {
  hostProject: HostProjectSchema;
  createRevisionEnabled?: boolean;
  hideHeader?: boolean;
  showActiveRevision?: boolean;
}) {
  const { hostProject, showActiveRevision } = props;
  const { paginationModel, setPaginationModel } = useDataGridState({});
  const { mutate: refetchHostProject } = useHostProject(hostProject.id);
  const [latestRevisionRefreshInterval, setLatestRevisionRefreshInterval] =
    useState(0);
  const [refreshInterval, setRefreshInterval] = useState(0);
  const {
    data: rows,
    isLoading,
    isValidating,
    mutate: refetchRevisions,
  } = useHostRevisions(
    hostProject.id,
    showActiveRevision
      ? { limit: 1, offset: 0, status: 'DEPLOYED' }
      : {
          offset: paginationModel.pageIndex * paginationModel.pageSize,
          limit: paginationModel.pageSize,
        },
    {
      refreshInterval,
      onSuccess: (data) => {
        const deployedRevision = data.find((r) => r.status === 'DEPLOYED');
        if (
          deployedRevision?.id !==
          hostProject.resource?.latest_active_revision
            ?.hosted_langserve_revision_id
        ) {
          refetchHostProject();
        }
      },
    }
  );

  const { data: latestRevision } = useHostRevisions(
    hostProject.id,
    {
      limit: 1,
      offset: 0,
    },
    {
      refreshInterval: latestRevisionRefreshInterval,
    }
  );

  // there are issues with SWR when we set refreshInterval to a callback function so instead
  // we use a combo of useEffect and useState and set the refreshInterval to a number
  useEffect(() => {
    const newInterval = rows?.some(
      (item) => getRevisionSimpleStatus(item.status) === 'pending'
    )
      ? 3000
      : 0;

    setRefreshInterval(newInterval);
  }, [rows]);

  useEffect(() => {
    const newInterval = latestRevision?.some(
      (item) => getRevisionSimpleStatus(item.status) === 'pending'
    )
      ? 3000
      : 0;

    setLatestRevisionRefreshInterval(newInterval);
  }, [latestRevision]);

  const rowsToDisplay = useMemo(
    () =>
      showActiveRevision
        ? // if there is no deployed revision, show the latest revision
          ([rows?.[0] ?? latestRevision?.[0]].filter(
            Boolean
          ) as HostRevisionSchema[])
        : rows,
    [showActiveRevision, latestRevision, rows]
  );

  const [revisionPeekId, setRevisionPeekId] =
    useStateFromSearchParams('revisionPeek');

  const [traceIdForLogs, setTraceIdForLogs] =
    useStateFromSearchParams('peekTraceIdForLogs');

  return (
    <div className="flex flex-col gap-2">
      {props.showActiveRevision && !!rowsToDisplay?.length && (
        <LatestRevisionWarningButton
          hostProjectId={props.hostProject.id}
          onRevisionPeekChange={setRevisionPeekId}
          onRevisionSuccess={refetchRevisions}
          isLatestRevisionQueued={latestRevision?.[0]?.status === 'QUEUED'}
        />
      )}
      <div className="w-full overflow-hidden rounded-lg border border-secondary bg-primary">
        {!props.showActiveRevision && (
          <div className="flex items-center justify-between border-b border-secondary bg-secondary p-5">
            <div className="flex flex-col">
              <h2 className="text-lg font-semibold">Revisions</h2>
              <p className="text-sm text-tertiary">
                All revisions associated with this deployment
              </p>
            </div>
            {props.createRevisionEnabled && (
              <RevisionCrudModalWithButton hostProject={props.hostProject} />
            )}
          </div>
        )}

        <HostRevisionsTable
          rows={rowsToDisplay}
          isLoading={isLoading}
          isValidating={isValidating}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          onRevisionPeekChange={setRevisionPeekId}
          revisionPeekId={revisionPeekId}
          hostProject={props.hostProject}
          emptyState={<NoRevisions />}
          showActiveRevision={props.showActiveRevision}
          hideHeader={props.hideHeader}
          paginationTotal={rows?.headers?.['x-pagination-total']}
          traceIdForLogs={traceIdForLogs}
          setTraceIdForLogs={setTraceIdForLogs}
          refetchRevisions={refetchRevisions}
        />
      </div>
    </div>
  );
}

function HostProjectTraceCount(props: { hostProject: HostProjectSchema }) {
  const timeFilter = useChartTimeFilter({
    timeRange: { duration: '7d' },
  });

  const {
    data: dashboard,
    mutate: mutateChartData,
    isLoading,
    error,
  } = usePrebuiltDashboardForSession(props.hostProject.tracer_session_id, {
    ...timeFilter.customChartsParams,
  });

  const chart = dashboard?.sub_sections
    ?.find((section) => section.id === 'sub-section-traces')
    ?.charts.find((chart) => chart.id === 'chart-trace-count');

  const seriesColorsMap = useSeriesColorsMap({
    dashboard,
    isPrebuilt: true,
  });

  if (isLoading)
    return (
      <div className="min-h-[420px]">
        <LinearProgress />
      </div>
    );
  if (error) return <ExpandableErrorAlert error={error} />;
  if (!chart) return null;
  return (
    <Chart
      key={chart.id}
      chart={chart}
      dashboardId={props.hostProject.tracer_session_id}
      openExistingChart={() => {}}
      mutateChartData={mutateChartData}
      timeFilter={timeFilter}
      hideProjectNames={true}
      hideExpandedView={true}
      hideChartHeader={true}
      isLastColumn={true}
      readOnly={true}
      seriesColorsMap={seriesColorsMap}
    />
  );
}

function HostProjectTraceCountCard(props: { hostProject: HostProjectSchema }) {
  const organizationId = useOrganizationId();
  const dashboardLink = `/${appOrganizationPath}/${organizationId}/${appDashboardsIndexPath}/${appProjectsPath}/${props.hostProject.tracer_session_id}`;
  return (
    <div className="flex flex-col rounded-lg border border-secondary bg-secondary">
      <div className="flex items-center justify-between p-5">
        <div className="flex flex-col">
          <h2 className="text-lg font-semibold">Trace Count</h2>
          <p className="text-sm text-tertiary">
            A summary chart showing successful, pending and erroring traces in
            the last 7 days.
          </p>
        </div>
        <Link to={dashboardLink}>
          <Button
            variant="outlined"
            color="secondary"
            rightDecorator={ArrowRightIcon}
            className="whitespace-nowrap pr-3"
          >
            All charts
          </Button>
        </Link>
      </div>
      <div className="relative mx-5 mb-5 flex-grow">
        <HostProjectTraceCount hostProject={props.hostProject} />
      </div>
    </div>
  );
}

function HostProjectRunsCard(props: { hostProject: HostProjectSchema }) {
  const organizationId = useOrganizationId();
  return (
    <div className="rounded-lg border border-secondary bg-secondary">
      <div className="flex items-center justify-between border-b border-secondary p-5">
        <div className="flex flex-col">
          <h2 className="text-lg font-semibold">Recent Traces</h2>
          <p className="text-sm text-tertiary">
            Recent traces (7D) for this deployment appear here. Go to the
            tracing project to see all traces.
          </p>
        </div>
        <Link
          to={`/${appOrganizationPath}/${organizationId}/${appProjectsPath}/${appSessionPath}/${props.hostProject.tracer_session_id}`}
        >
          <Button
            variant="outlined"
            color="secondary"
            leftDecorator={ProjectIcon}
            rightDecorator={ArrowRightIcon}
            className="whitespace-nowrap px-3"
          >
            See tracing project
          </Button>
        </Link>
      </div>

      <HostProjectRunsTable hostProject={props.hostProject} />
    </div>
  );
}

const HostProjectPage = ({
  hostProjectId,
  createRevisionEnabled,
  customHeaders,
  setCustomHeaders,
}: {
  hostProjectId: string | undefined;
  createRevisionEnabled?: boolean;
  customHeaders: Record<string, string>;
  setCustomHeaders: Dispatch<SetStateAction<Record<string, string>>>;
}) => {
  const deploymentFormType = useDeploymentFormType();
  const organizationId = useOrganizationId();
  const {
    data: hostProject,
    isLoading,
    error,
  } = useHostProject(hostProjectId ?? null);

  const [projectStatsOpen, setProjectStatsOpen] = useState(true);

  const [actionsCompact, setActionsCompact] = useState(false);
  const actionsContainerRef = useResizeObserver<HTMLDivElement>((entry) => {
    const width = entry[0]?.contentRect.width;
    // if project stats are not open, we show an extra "details" button
    const widthThreshold = projectStatsOpen ? 350 : 400;
    if (width && width < widthThreshold) {
      setActionsCompact(true);
    } else {
      setActionsCompact(false);
    }
  });

  const getDeploymentPlatformId = (): string => {
    const metadata = hostProject?.metadata || {};
    if ('platform' in metadata) {
      return metadata.platform;
    } else {
      return 'k8s_operator';
    }
  };

  const k8sCluster = hostProject?.metadata.k8s_cluster;

  const showMigrationBanner =
    deploymentFormType === 'cloud' &&
    (!k8sCluster || OLD_CLUSTERS.has(k8sCluster));

  const copyItems = useMemo(() => {
    if (!hostProject) return [];
    return [
      { prefix: 'Deployment ID', item: hostProject.id },
      ...(hostProject.resource?.latest_revision?.hosted_langserve_revision_id
        ? [
            {
              prefix: 'Latest Revision ID',
              item: hostProject.resource.latest_revision
                .hosted_langserve_revision_id,
            },
          ]
        : []),
    ];
  }, [hostProject]);

  const showMonitoringTab =
    !!hostProject?.resource?.url &&
    getDeploymentPlatformId() === 'k8s_operator' &&
    deploymentFormType === 'cloud';

  if (isLoading)
    return (
      <div className="flex w-full items-start justify-start">
        <LinearProgress />
      </div>
    );
  if (error)
    return (
      <div className="flex w-full items-start justify-start p-4">
        <ExpandableErrorAlert error={error} />
      </div>
    );

  if (!hostProject) return <div>No host project</div>;

  const studioLink = `/${appGraphIndexPath}/thread?organizationId=${organizationId}&hostProjectId=${encodeURIComponent(
    hostProject.id
  )}`;

  const { name } = hostProject;
  return (
    <div className="flex w-full grow overflow-x-hidden border-t border-secondary">
      <div className="flex flex-1 flex-col overflow-hidden px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <PageTitle icon={<RocketShipIcon />}>
              <TextOverflowTooltip>{name}</TextOverflowTooltip>
            </PageTitle>

            <CopyMultiButton copyItems={copyItems} textSize="xs" />
          </div>
          <div
            className="flex grow items-center justify-end gap-2"
            ref={actionsContainerRef}
          >
            {!projectStatsOpen && (
              <Button
                onClick={() => setProjectStatsOpen(!projectStatsOpen)}
                className="rounded-md px-1 hover:bg-primary-hover"
                variant="plain"
                color="secondary"
              >
                Details
              </Button>
            )}
            {createRevisionEnabled && (
              <>
                <HostProjectSettingsModalWithButton
                  hostProject={hostProject}
                  deploymentPlatformId={getDeploymentPlatformId()}
                  customHeaders={customHeaders}
                  setCustomHeaders={setCustomHeaders}
                />
                <RevisionCrudModalWithButton
                  hostProject={hostProject}
                  compact={actionsCompact}
                />
              </>
            )}

            {hostProject.resource?.url &&
              (actionsCompact ? (
                <Link to={studioLink}>
                  <IconButton icon={PlayCircleIcon} label="LangGraph Studio" />
                </Link>
              ) : (
                <Link to={studioLink} className="flex items-center gap-2">
                  <Button leftDecorator={PlayCircleIcon}>Studio</Button>
                </Link>
              ))}
          </div>
        </div>
        {showMigrationBanner && (
          <div className="w-full">
            <MigrationBanner />
          </div>
        )}

        <TabGroup>
          <TabList className="min-h-[40px] overflow-x-auto">
            <TabLabel label="Overview" />
            <TabLabel label="Revisions" />
            {showMonitoringTab && <TabLabel label="Monitoring" />}
            {hostProject.resource?.url && <TabLabel label="Assistants" />}
            {hostProject.resource?.url && <TabLabel label="Threads" />}
            {hostProject.resource?.url && <TabLabel label="Crons" />}
          </TabList>
          <TabPanels className="overflow-y-auto pt-4">
            <TabPanel>
              <div className="flex flex-col gap-4">
                <HostProjectRevisionsCard
                  hostProject={hostProject}
                  showActiveRevision={true}
                  hideHeader={true}
                />

                <HostProjectTraceCountCard hostProject={hostProject} />

                <HostProjectRunsCard hostProject={hostProject} />
              </div>
            </TabPanel>
            <TabPanel>
              <HostProjectRevisionsCard
                hostProject={hostProject}
                createRevisionEnabled={createRevisionEnabled}
              />
            </TabPanel>
            {showMonitoringTab && (
              <TabPanel>
                <MonitoringPanel hostProjectId={hostProject.id} />
              </TabPanel>
            )}
            <TabPanel>
              <AgentsPanel hostProjectId={hostProject.id} />
            </TabPanel>
            <TabPanel>
              <ThreadsPanel hostProjectId={hostProject.id} />
            </TabPanel>
            <TabPanel>
              <CronsPanel />
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </div>
      {projectStatsOpen && (
        <ProjectStats
          hostProject={hostProject}
          onClose={() => setProjectStatsOpen(false)}
        />
      )}
    </div>
  );
};

export default function Page() {
  const { hostProjectId } = useParams();
  const { authorize } = usePermissions();

  const [headersForProject, setHeadersForProject] = useLocalStorageState(
    `${STUDIO_HEADERS_LOCAL_STORAGE_KEY}:${hostProjectId}`,
    {},
    z.record(z.string(), z.string())
  );

  const client = useCreateStudioClient({
    projectId: hostProjectId,
    headers: headersForProject,
  });

  return (
    <LangGraphContext.Provider value={client}>
      <div className="flex h-[100vh] flex-col">
        <div className="px-4 py-3">
          <Breadcrumbs />
        </div>
        <HostProjectPage
          hostProjectId={hostProjectId}
          createRevisionEnabled={authorize('deployments:update')}
          customHeaders={headersForProject}
          setCustomHeaders={setHeadersForProject}
        />
      </div>
    </LangGraphContext.Provider>
  );
}
